import { Procedure, Worksheet, CodeSet, Diagnosis } from './domain';
interface WithDiagnoses {
  diagnoses?: Diagnosis[];
}
export function updateIcdLineNumbers(v: Diagnosis[]): Diagnosis[] {
  v.forEach((u, i) => (u.lineNumber = i + 1));
  return v;
}
export function diagnosesLinkString(w: WithDiagnoses): string {
  if (!w.diagnoses || !w.diagnoses.length) {
    return '';
  }
  return w.diagnoses.map((d) => d.lineNumber).join(',');
}

export function diagnosisLinks(links: string): number[] {
  if (!links) {
    return [];
  }
  return [
    ...new Set(
      links
        .trim()
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter((n) => !isNaN(n))
    ),
  ].sort((a, b) => a - b);
}

export function sanitizeDiagnosisLinksByLineNumber(links: string, w?: WithDiagnoses): string {
  const dx = diagnosisLinks(links);
  if (w) {
    return w.diagnoses
      .filter((d) => dx.some((l) => l === d.lineNumber))
      .map((d) => d.lineNumber)
      .join(',');
  }
  return dx.join(',');
}

export function sanitizeDiagnosisLinksByLinkOrder(links: string, w?: Worksheet): string {
  const dx = diagnosisLinks(links);
  if (w?.diagnoses?.length) {
    const lineNumbers = w.diagnoses.map((d) => d.lineNumber);
    return dx.filter((d) => lineNumbers.some((l) => l === d)).join(',');
  }
  return dx.join(',');
}

export function diagnosisCodesByLinkString(w: WithDiagnoses, links: string): string[] {
  if (!w.diagnoses || !w.diagnoses.length) {
    return [];
  }
  const lineNumbers: number[] = diagnosisLinks(links);
  return w.diagnoses.filter((d) => lineNumbers.some((l) => l === d.lineNumber)).map((d) => d.diagnosisCode);
}

export function diagnosisLinkString(diagnoses: Diagnosis[], codes: string[]): string {
  if (!diagnoses || !diagnoses.length || !codes || !codes.length) {
    return '';
  }
  const lineNumbers = diagnoses.filter((d) => codes.some((l) => l === d.diagnosisCode)).map((d) => d.lineNumber);

  return lineNumbers.join(',');
}

export const NO_DIAGNOSIS_ERROR = 'A worksheet must contain a diagnosis before adding a procedure';

type CptDxConf = {
  emLinks?: boolean;
  cptLinks?: boolean;
  activeOnly?: boolean;
};

export function setProcedureDiagnoses(sheet: WithDiagnoses, p: Procedure, conf?: CptDxConf): Procedure {
  const { activeOnly, emLinks, cptLinks } = {
    ...{ emLinks: true, cptLinks: true },
    ...conf,
  };

  sheet.diagnoses = sheet.diagnoses || [];
  sheet.diagnoses = activeOnly ? sheet.diagnoses.filter((v) => v.active) : sheet.diagnoses;

  let diagnoses = updateIcdLineNumbers(sheet.diagnoses);
  const w = { diagnoses };

  let links = p.links || '';
  let dx = sanitizeDiagnosisLinksByLinkOrder(links, w);

  // console.log('p links', p.links, 'sanitized', dx);

  if (!dx.length) {
    if (p.isEMCode) {
      if (emLinks) {
        dx = diagnosesLinkString(w);
      } else {
        dx = '1';
      }
    } else {
      if (cptLinks) {
        dx = diagnosesLinkString(w);
      } else {
        dx = '1';
      }
    }
  }
  p.diagnosisList = diagnosisCodesByLinkString(w, dx);
  p.links = dx;
  return p;
}

export function setAllDiagnosesOnEmProcedures(w: Worksheet): void {
  w.diagnoses = w.diagnoses || [];
  w.procedures = w.procedures || [];

  if (w.procedures.length) {
    diagnosesLinkString(w as WithDiagnoses);
    w.procedures.filter((cpt) => cpt.isEMCode).forEach((cpt) => setProcedureDiagnoses(w as WithDiagnoses, cpt));
  }
}

export function addOneIcdToEmProcedures(icd: Diagnosis, procedures: Procedure[]): void {
  if (procedures.length) {
    procedures
      .filter((cpt) => cpt.isEMCode)
      .forEach((cpt) => {
        cpt.diagnosisList = cpt.diagnosisList || [];
        if (cpt.diagnosisList.includes(icd.diagnosisCode)) {
          return;
        }
        cpt.diagnosisList.push(icd.diagnosisCode);
        cpt.links ||= '';
        cpt.links += ',' + icd.lineNumber;
        cpt.links = sanitizeDiagnosisLinksByLineNumber(cpt.links);
      });
  }
}

export function setAllDiagnosesOnEmptyCpts(w: Worksheet): void {
  w.diagnoses = w.diagnoses || [];
  w.procedures = w.procedures || [];
  if (w.procedures.length) {
    diagnosesLinkString(w as WithDiagnoses);

    const empty = w.procedures.filter((v) => {
      v.links = sanitizeDiagnosisLinksByLineNumber(v.links, w);
      return !v.links || v.links.trim().length < 1;
    });
    empty.forEach((cpt) => setProcedureDiagnoses(w as WithDiagnoses, cpt));
  }
}

export function setFirstDiagnosisOnEmptyCpts(w: Worksheet): void {
  w.diagnoses = w.diagnoses || [];
  w.procedures = w.procedures || [];
  if (w.procedures.length) {
    const empty = w.procedures.filter((v) => {
      v.links = sanitizeDiagnosisLinksByLineNumber(v.links, w);
      return !v.links || v.links.trim().length < 1;
    });
    empty.forEach((cpt) => (cpt.links = '1'));
  }
}

export function addIcdToAllCpts(icd: Diagnosis, w: Worksheet): void {
  w.diagnoses = w.diagnoses || [];
  w.procedures = w.procedures || [];
  const exists = w.diagnoses.some((d) => d.diagnosisCode === icd.diagnosisCode);
  if (!isNaN(icd?.lineNumber) && w.procedures.length && !exists) {
    w.procedures.forEach((cpt) => {
      cpt.links = cpt.links || '';
      cpt.links = cpt.links + (cpt.links.length ? ',' : ' ') + icd?.lineNumber;
      setProcedureDiagnoses(w as WithDiagnoses, cpt);
    });
  }
}

export function diagnosis(c: CodeSet, workflow: string): Diagnosis {
  const { note } = c;

  let auditMap = c.auditMap || {};

  if (workflow) {
    auditMap[workflow] = {...c.audit || {}};
    auditMap[workflow].disagreementReasons = undefined;
  }

  return {
    auditMap,
    diagnosisId: c?.diagnosisId,
    audit: c.audit || undefined,
    active: c.active === undefined ? true : c.active,
    diagnosisCode: c.dxWithDecimal || c.code || c.diagnosisCode,
    lineNumber: c.lineNumber,
    dxDescription: c.description ? c.description.longDescription : c.dxDescription || c.longDescription,
    stickyNote: note ? note.note : c.stickyNote,
    presentOnAdmission: c.presentOnAdmission,
  };
}
